/**
 * Hooks React Query da identidade visual da própria clinic.
 *
 * RLS: `clinics_select_member` lê, `clinics_update_admin` UPDATE.
 *
 * Mantemos isolado (não junto com `clinic-profile` queries) porque
 * tema/identidade tem padrão de uso diferente: lido EM TODA navegação
 * (PaletteThemeProvider) e atualizado raramente. Cache mais agressivo
 * (5min) faz sentido pro read; profile (10s) não.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { supabase } from '../../lib/supabase.js';

import { resolvePalette, type PaletteKey } from './palettes.js';

export const CLINIC_APPEARANCE_KEYS = {
  theme: (clinicId: string) => ['clinic-appearance', 'theme', clinicId] as const,
};

export interface ClinicThemeRow {
  id: string;
  theme_palette: PaletteKey;
  /** Logo URL — preservada pra futura PR de upload de logo (M2-04b). */
  logo_url: string | null;
}

/** Lê paleta + logo da clínica. Cache 5min — tema raramente muda no
 * meio de uma sessão. */
export function useClinicTheme(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: CLINIC_APPEARANCE_KEYS.theme(clinicId ?? ''),
    enabled: Boolean(clinicId),
    staleTime: 5 * 60 * 1_000,
    queryFn: async (): Promise<ClinicThemeRow | null> => {
      if (!clinicId) return null;
      const { data, error } = await supabase
        .from('clinics')
        .select('id, theme_palette, logo_url')
        .eq('id', clinicId)
        .is('deleted_at', null)
        .maybeSingle();
      if (error) throw new Error(error.message);
      if (!data) return null;
      // Cast `unknown` first — coluna `theme_palette` foi adicionada na
      // migration 0066 desta PR; supabase types ainda não foram
      // regenerados, então o select tipa `data` como SelectQueryError.
      // Após a primeira regeneração de types pós-merge, o cast pode ser
      // simplificado pra `data as { id; theme_palette; logo_url; }`.
      const row = data as unknown as {
        id: string;
        theme_palette: string;
        logo_url: string | null;
      };
      return {
        id: row.id,
        theme_palette: resolvePalette(row.theme_palette),
        logo_url: row.logo_url,
      };
    },
  });
}

/** Atualiza paleta. RLS valida owner/admin. */
export function useUpdateClinicPalette() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { clinicId: string; palette: PaletteKey }) => {
      const { error } = await supabase
        .from('clinics')
        // Cast `as never` — coluna `theme_palette` foi adicionada na
        // migration 0066 desta PR; supabase types ainda não foram
        // regenerados, então `update` rejeita a chave em compile-time.
        // RLS valida runtime; após regeneração o cast pode sair.
        .update({ theme_palette: input.palette } as never)
        .eq('id', input.clinicId);
      if (error) throw new Error(error.message);
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: CLINIC_APPEARANCE_KEYS.theme(variables.clinicId) });
    },
  });
}

/** Bucket criado em migration 0070. Público (URL pública aparece em
 * email/PDF/portal do paciente sem login) com size limit 2MB e MIME
 * restrito a imagens. */
const LOGO_BUCKET = 'clinic-logos';

/** Tamanhos limites validados no client ANTES do upload. Storage também
 * valida (file_size_limit no bucket), mas validar antes evita upload
 * inútil + dá feedback rápido ao usuário. */
export const LOGO_MAX_BYTES = 2 * 1024 * 1024; // 2MB
export const LOGO_ALLOWED_MIME = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/svg+xml',
] as const;
export type LogoAllowedMime = (typeof LOGO_ALLOWED_MIME)[number];

/** Faz upload do logo no bucket público + atualiza `clinics.logo_url`
 * com a URL pública correspondente. RLS do storage valida que o usuário
 * é clinic_owner/clinic_admin do tenant. RLS de `clinics` faz a mesma
 * validação no UPDATE.
 *
 * **Path pattern fixo**: `{clinic_id}/logo.{ext}` — sobrescreve no
 * re-upload. Nome estável evita poluir bucket com versões antigas.
 *
 * **Cache busting**: a URL pública é a mesma após sobrescrever (CDN do
 * Supabase pode servir versão velha). Salvamos `?v={timestamp}` na
 * coluna `logo_url` pra forçar reload em browsers/email clients.
 *
 * **Idempotência**: `upsert: true` na opção do Storage faz o upload
 * sobrescrever se já existir. Sem isso, segundo upload retornaria erro
 * 409 conflict.
 */
export function useUploadClinicLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { clinicId: string; file: File }) => {
      const ext = extensionFromMime(input.file.type);
      if (!ext) {
        throw new Error(`Tipo de arquivo não suportado: ${input.file.type}`);
      }
      const path = `${input.clinicId}/logo.${ext}`;

      // 1. Upload pro bucket — sobrescreve se já existir (upsert: true)
      const upload = await supabase.storage
        .from(LOGO_BUCKET)
        .upload(path, input.file, { upsert: true, contentType: input.file.type });
      if (upload.error) throw new Error(`upload: ${upload.error.message}`);

      // 2. Resolve a URL pública + cache buster
      const publicUrl = supabase.storage.from(LOGO_BUCKET).getPublicUrl(path).data.publicUrl;
      const cacheBuster = Date.now();
      const finalUrl = `${publicUrl}?v=${cacheBuster}`;

      // 3. Atualiza `clinics.logo_url` (RLS valida que é owner/admin)
      const { error: updateErr } = await supabase
        .from('clinics')
        .update({ logo_url: finalUrl })
        .eq('id', input.clinicId);
      if (updateErr) throw new Error(`update logo_url: ${updateErr.message}`);

      return { logoUrl: finalUrl };
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: CLINIC_APPEARANCE_KEYS.theme(variables.clinicId) });
    },
  });
}

/** Remove o logo: deleta do bucket E zera `clinics.logo_url`.
 *
 * Tenta deletar **todos os ext possíveis** do path padrão pra cobrir o
 * caso de o owner já ter feito upload com extensão diferente antes
 * (ex: png agora, jpg antes — sobrou o jpg no bucket). Storage não
 * retorna erro pra arquivo inexistente, então é seguro fazer N delete.
 */
export function useDeleteClinicLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { clinicId: string }) => {
      const paths = (['png', 'jpg', 'jpeg', 'webp', 'svg'] as const).map(
        (ext) => `${input.clinicId}/logo.${ext}`,
      );
      const { error: deleteErr } = await supabase.storage.from(LOGO_BUCKET).remove(paths);
      // Storage não erra pra arquivo inexistente, mas se erro real
      // acontecer (auth, network), propagamos.
      if (deleteErr) throw new Error(`delete logo: ${deleteErr.message}`);

      const { error: updateErr } = await supabase
        .from('clinics')
        .update({ logo_url: null })
        .eq('id', input.clinicId);
      if (updateErr) throw new Error(`reset logo_url: ${updateErr.message}`);
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: CLINIC_APPEARANCE_KEYS.theme(variables.clinicId) });
    },
  });
}

/** Mapeia MIME type → extensão de arquivo pra path no bucket.
 * Retorna null se o MIME não for suportado. */
function extensionFromMime(mime: string): string | null {
  switch (mime) {
    case 'image/png':
      return 'png';
    case 'image/jpeg':
      return 'jpg';
    case 'image/webp':
      return 'webp';
    case 'image/svg+xml':
      return 'svg';
    default:
      return null;
  }
}
